import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  getInvitedUsers,
  inviteUsers,
  reinviteUser,
  deleteInvitedUser
} from "@/services/apis/rbacApi";
import { InvitedUsersParams, Role } from "./useRbac";

export interface Brand {
  selected: boolean;
  companyId: number;
  name: string;
  brandId: number;
  userId: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface UseCaseResponse {
  id: number;
  name: string;
  displayName: string;
  rank: number;
  useCaseType: string;
}

export interface InvitedUser {
  email: string;
  firstName: string;
  lastName: string;
  userId: number;
  status: string;
  message: string;
  productType: string;
  invited: boolean;
  roles: Role[];
  brands: Brand[];
  useCaseResponses: UseCaseResponse[];
  verificationLink?: string;
}

export interface InvitedUsersResponse {
  content: InvitedUser[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  pagesRemaining: number;
}

export interface InviteUserPayload {
  firstName: string;
  lastName: string;
  email: string;
  roleIds: number[];
  productType: string;
}

export const useInvitedUsers = (initialParams: InvitedUsersParams) => {
  const queryClient = useQueryClient();
  const [userParams, setUserParams] = useState<InvitedUsersParams>(initialParams);
  const { companyId, productType, page, size, search, roleId } = initialParams;

  useEffect(() => {
    setUserParams(initialParams);
  }, [companyId, productType, page, size, search, roleId]);

  const { data: invitedUsersData, isLoading, isPending, error } = useQuery<InvitedUsersResponse, Error>({
    queryKey: ["invitedUsers", userParams],
    queryFn: () => getInvitedUsers(userParams),
    enabled: !!userParams.companyId,
    staleTime: 1000 * 60, // 1 minute
  });

  const inviteUserMutation = useMutation({
    mutationFn: inviteUsers,
    onSuccess: () => {
      // When an invite is successful, invalidate the "invitedUsers" query to refetch the list
      queryClient.invalidateQueries({ queryKey: ["invitedUsers"] });
    },
  });

  const reinviteUserMutation = useMutation({
    mutationFn: reinviteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitedUsers"] });
    },
  });

  const deleteInvitedUserMutation = useMutation({
    mutationFn: deleteInvitedUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitedUsers"] });
    },
  });

  const fetchNextPage = () => {
    if (invitedUsersData && !invitedUsersData.last) {
      setUserParams(prev => ({ ...prev, page: (prev.page || 0) + 1 }));
    }
  };

  const fetchPreviousPage = () => {
    if (invitedUsersData && !invitedUsersData.first) {
      setUserParams(prev => ({ ...prev, page: (prev.page || 0) - 1 }));
    }
  };

  const setPage = (page: number) => {
    setUserParams(prev => ({ ...prev, page }));
  };

  const hasNextPage = invitedUsersData ? !invitedUsersData.last : false;
  const hasPreviousPage = invitedUsersData ? !invitedUsersData.first : false;

  return {
    invitedUsers: invitedUsersData?.content || [],
    pagination: {
      totalElements: invitedUsersData?.totalElements,
      totalPages: invitedUsersData?.totalPages,
      currentPage: invitedUsersData?.currentPage,
      pageSize: invitedUsersData?.pageSize,
      hasNextPage,
      hasPreviousPage,
      fetchNextPage,
      fetchPreviousPage,
      setPage,
    },
    isLoading,
    isPending,
    error,
    inviteUser: inviteUserMutation.mutate,
    isInviting: inviteUserMutation.isPending,
    inviteError: inviteUserMutation.error,
    reinviteUser: reinviteUserMutation.mutate,
    isReinviting: reinviteUserMutation.isPending,
    deleteInvitedUser: deleteInvitedUserMutation.mutate,
    isDeletingUser: deleteInvitedUserMutation.isPending,
    setUserParams,
  };
};

// One request page for the "all users" lookup below — company user lists are
// small enough that this comfortably covers almost every company in one call;
// useCompanyUsersList's queryFn fetches the rest (in parallel, once
// `totalPages` is known) instead of silently dropping them.
const COMPANY_USERS_LOOKUP_PAGE_SIZE = 500;
// Safety cap on `totalPages` (a backend reporting an absurd count should not
// fire an unbounded burst of parallel requests), not an expected ceiling —
// 50 pages at 500/page is 25,000 users.
const COMPANY_USERS_LOOKUP_MAX_PAGES = 50;

/**
 * Every invited user in the company, unpaginated for callers that need the
 * full roster (email lookups, "which users can be added to a group" lists).
 *
 * Group-membership endpoints (`/data-groups/{id}/members`) return only
 * `user_id` — this is the one place that knows how to turn that back into a
 * displayable user, so nothing above it needs to fetch the user list itself.
 */
export const useCompanyUsersList = (companyId?: number) => {
  const { data, isLoading, isError, refetch } = useQuery<InvitedUser[]>({
    // Nested under the "invitedUsers" key (not a sibling of it) so the
    // invite/reinvite/edit/delete mutations' existing
    // `invalidateQueries({ queryKey: ["invitedUsers"] })` also refreshes this
    // lookup — as a sibling key it never matched that invalidation and could
    // show a stale roster (e.g. a just-deleted user still listed as
    // assignable) until its own staleTime happened to expire.
    queryKey: ["invitedUsers", "companyUsersLookup", companyId],
    queryFn: async () => {
      const fetchPage = (page: number) =>
        getInvitedUsers({
          companyId: companyId as number,
          productType: "IQ",
          page,
          size: COMPANY_USERS_LOOKUP_PAGE_SIZE,
        });

      // Page 0 first (sequentially) — only it tells us `totalPages`, so the
      // rest can't be requested before this one answers. Once known, every
      // remaining page is independent (page N doesn't depend on page N-1's
      // result), so they fetch in parallel instead of one-after-another.
      const firstPage: InvitedUsersResponse = await fetchPage(0);
      const allUsers: InvitedUser[] = [...(firstPage.content ?? [])];
      const totalPages = Math.min(firstPage.totalPages ?? 1, COMPANY_USERS_LOOKUP_MAX_PAGES);

      if (totalPages > 1) {
        const remainingPages = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 1)),
        );
        remainingPages.forEach((response) => allUsers.push(...(response.content ?? [])));
      }

      return allUsers;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60,
  });

  return { users: data ?? [], isLoading, isError, refetch };
};

/** userId -> email for every invited user in the company. See {@link useCompanyUsersList}. */
export const useUserEmailsById = (companyId?: number) => {
  const { users } = useCompanyUsersList(companyId);

  return useMemo(() => {
    const map = new Map<number, string>();
    users.forEach((user) => map.set(user.userId, user.email));
    return map;
  }, [users]);
};
